// R115 A1 — the MTR view's surfaces: inspector sections (listing → MTR rates &
// listings, stay/tenant → MTR tenant, lead → pipeline / tours / details), the
// two quick popovers (try-MTR card, tour card), the form sheets (+ Lead, tour,
// note, lost/ended reason, reschedule, rent ledger) and the one fetch helper.
// Every write goes through the MTR module's EXISTING routes (POST /api/mtr/leads,
// /leads/:id, /leads/:id/tours…, /channels, /tenants/:id/payment) and then
// asks the page to reload the 'mtr' layer (+ the open inspector).
// Kebab menus MOVE to <body> on open, so their item handlers are bound per
// element after every render (never through a delegated root listener).
import type { CalendarCtx, EntityRef } from '../registry';
import { kebabHtml, bindKebabs, closeKebab, type KebabItem } from '../../../shared/components/Kebab';
import { mountRentLedger } from '../../../shared/components/MtrRentLedger';
import { whoFrom } from '../../../shared/lib/mtrTenant';
import {
  ov, money, stageLabel, firstName, monthName, timeLabel, wall, verdict, recOf, listedOf, isOpenLead, tenantsForStay,
  CHANNELS, STAGES_FALLBACK, TERMINAL, SETTLED, TOUR_LABEL,
  type MtrOverlay, type MtrTenant, type MtrLead, type MtrLeadTour, type MtrTour, type MtrListedCh,
} from './model';

// ── module state (reset with the page) ─────────────────────────────────────
export const st = {
  dirty: false,
  saving: 0,
  people: null as Array<{ email: string; full_name: string }> | null,
  peopleP: null as Promise<void> | null,
  leadCache: new Map<string, MtrLead>(),
  tenantCache: new Map<string, any>(),
};
export function resetState(): void { st.dirty = false; st.saving = 0; st.leadCache.clear(); st.tenantCache.clear(); }
// R115 review: an abandoned listed-rate edit must not park the auto-refresh veto — its dirtiness is read from the
// LIVE inputs (a closed panel has none), never from a module flag that nothing clears
const listedDirty = (): boolean => [...document.querySelectorAll<HTMLInputElement>('[data-mtrc-listed]')].some(i => i.isConnected && i.value.trim() !== (i.dataset.orig || ''));
export const busy = () => st.dirty || st.saving > 0 || listedDirty();

// ── fetch ──────────────────────────────────────────────────────────────────
export interface ApiRes { ok: boolean; status: number; j: any }
export async function api(method: 'GET' | 'POST' | 'PUT' | 'DELETE', url: string, body?: unknown): Promise<ApiRes> {
  st.saving += method === 'GET' ? 0 : 1;
  try {
    const r = await fetch(url, { method, headers: body === undefined ? undefined : { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, j: j || {} };
  } catch {
    return { ok: false, status: 0, j: { error: 'Network error — check the connection and try again.' } };
  } finally {
    if (method !== 'GET') st.saving = Math.max(0, st.saving - 1);
  }
}
const errText = (r: ApiRes, what: string) => (r.j && r.j.error ? String(r.j.error) : r.status ? `${what} failed (${r.status})` : 'Network error — nothing was saved.');
const reloadMtr = (ctx: CalendarCtx) => ctx.reload({ layers: ['mtr'], inspector: true });

function loadPeople(): Promise<void> {
  if (st.people) return Promise.resolve();
  if (!st.peopleP) {
    st.peopleP = api('GET', '/api/tasks/assignees').then(r => {
      st.people = Array.isArray(r.j && r.j.people) ? r.j.people.filter((p: any) => p && p.email).map((p: any) => ({ email: String(p.email), full_name: String(p.full_name || p.email) })) : [];
    }).catch(() => { st.people = []; });
  }
  return st.peopleP;
}

// The safe lead shape from a full lead view (a write's answer) — names + pipeline only.
export function safeLead(v: any): MtrLead | null {
  if (!v || !v.id) return null;
  return {
    id: String(v.id), name: String(v.name || 'Renter'), stage: String(v.stage || 'new'), property_interest: v.property_interest ? String(v.property_interest) : null,
    move_in: /^\d{4}-\d{2}-\d{2}$/.test(String(v.move_in || '')) ? v.move_in : null, term_months: v.term_months ?? null, offer_price: v.offer_price ?? null,
    stage_changed_at: v.stage_changed_at || null, created_at: v.created_at || null, updated_at: v.updated_at || null,
    tours: (Array.isArray(v.tours) ? v.tours : []).map((t: any) => ({ id: t.id, at: t.at, duration_min: t.duration_min || 60, status: t.status, with: t.with || null, note: t.note || null, listing_id: t.listing_id ? String(t.listing_id) : null })),
    tours_count: Array.isArray(v.tours) ? v.tours.length : 0, next_tour: v.next_tour || null, reopen_to: v.reopen_to || null,
  };
}
/** The NEWEST copy of a lead: a write's answer (st.leadCache) beats an overlay loaded before it (R115 review — a stale
 *  overlay copy made the next stage move fail with a false 409). */
export function leadOf(ctx: Pick<CalendarCtx, 'layer'>, id: string): MtrLead | null {
  const o = ov(ctx);
  const a = (o && o.leads.find(l => l.id === id)) || null;
  const b = st.leadCache.get(id) || null;
  const ts = (v: unknown) => { const t = Date.parse(String(v || '')); return Number.isFinite(t) ? t : -Infinity; };
  if (a && b) return ts(b.updated_at) > ts(a.updated_at) ? b : a;
  return a || b;
}
const stagesOf = (o: MtrOverlay | null) => (o && Array.isArray(o.stages) && o.stages.length ? o.stages : STAGES_FALLBACK);
const homeName = (ctx: CalendarCtx, id: string | null | undefined) => (id ? (ctx.property(id)?.name || `home ${id}`) : 'no home yet');

// One deep link into the MTR page (one-shot sessionStorage keys MtrPage reads in render()).
export function goMtr(ctx: CalendarCtx, tab: 'vacancy' | 'rates' | 'leads' | 'tenants', leadId?: string): void {
  try {
    sessionStorage.setItem('mtr_tab', tab);
    if (leadId) sessionStorage.setItem('mtr_open_lead', leadId);
  } catch { /* private mode: the page opens on its default tab */ }
  ctx.go('mtr');
}

function bindAct(root: HTMLElement, sel: string, fn: (el: HTMLElement) => void): void {
  root.querySelectorAll<HTMLElement>(sel).forEach(el => {
    if ((el as any).__mtrcBound) return;
    (el as any).__mtrcBound = true;
    el.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); closeKebab(); fn(el); });
  });
}

// ══ listing → "MTR rate & listings" (also the try-MTR quick card) ════════════
function listedRow(ctx: CalendarCtx, id: string, ch: (typeof CHANNELS)[number], x: MtrListedCh | null | undefined, rate: number | null): string {
  const e = ctx.esc;
  const cur = x && x.listed_rate != null ? String(x.listed_rate) : '';
  const v = verdict(x && x.listed_rate, rate);
  const meta: string[] = [];
  if (x && x.listed_rate != null) meta.push(`set${x.listed_rate_by ? ` by ${e(String(x.listed_rate_by).split('@')[0])}` : ''}${x.listed_rate_at ? ` · ${e(ctx.fmt.md(String(x.listed_rate_at).slice(0, 10)))}` : ''}`);
  if (v) meta.push(`<span class="mtrc-vd mtrc-vd-${v.v}" title="vs the recommended rate — good ≥ 100%, close ≥ 95% (the owner's 5% rule)">${v.pct}% of rec.</span>`);
  if (x && x.scraped_price) meta.push(`page shows ${money(x.scraped_price)}`);
  if (x && x.url) meta.push(`<a href="${e(x.url)}" target="_blank" rel="noopener">listing&nbsp;↗</a>`);
  return `<div class="mtrc-lrow" data-mtrc-lrow="${e(ch.key)}">
    <label class="mtrc-lname" for="mtrc-l-${e(id)}-${e(ch.key)}">${e(ch.label)}</label>
    <span class="mtrc-money"><span aria-hidden="true">$</span><input id="mtrc-l-${e(id)}-${e(ch.key)}" type="number" inputmode="decimal" min="100" step="25" value="${e(cur)}" placeholder="${e(x && x.scraped_price ? String(x.scraped_price) : '/mo')}" data-mtrc-listed="${e(ch.key)}" data-orig="${e(cur)}" aria-label="${e(ch.label)} listed rate per month"></span>
    <button type="button" class="btn btn-secondary btn-sm mtrc-save" data-mtrc-save="${e(ch.key)}" disabled>Save</button>
    ${meta.length ? `<div class="mtrc-lmeta">${meta.join(' · ')}</div>` : ''}
  </div>`;
}
export function ratesHtml(ctx: CalendarCtx, id: string, opts: { card?: boolean } = {}): string {
  const o = ov(ctx);
  const e = ctx.esc;
  if (!o) return '<div class="mtrc-muted">MTR data isn’t loaded — switch to the MTR view.</div>';
  const v = o.vacancies.find(x => x.listing_id === id) || null;
  const rec = recOf(o, id);
  const listed = listedOf(o, id);
  const mode = v ? (v.mtr_mode === 'dedicated' ? '<span class="mtrc-pill mtrc-pill-bad">MTR-only</span>' : '<span class="mtrc-pill">Seasonal MTR</span>') : '<span class="mtrc-pill">Not in the MTR set</span>';
  const why = v && !opts.card ? `<span class="mtrc-why">${e(v.try_mtr ? v.why : v.status === 'leased' ? 'Leased — open-ended tenant' : v.vacant_from ? `${v.why} · not a try-MTR window` : v.why)}</span>` : '';
  const recHtml = rec && rec.rate != null
    ? `<div class="mtrc-rec"><div class="mtrc-k">Recommended</div><div class="mtrc-recv"><b>${money(rec.rate)}</b>/mo <span class="mtrc-src mtrc-src-${e(rec.source)}">${rec.source === 'manual' ? 'manual' : 'AirROI'} · ${e(monthName(rec.month))}</span></div><div class="mtrc-basis">${e(rec.basis)}</div></div>`
    : `<div class="mtrc-rec mtrc-rec-none"><div class="mtrc-k">Recommended</div><div>No rate yet — <button type="button" class="mtrc-link" data-mtrc-go="rates">set one on Rates</button></div>${rec && rec.placeholder ? '<div class="mtrc-basis">Only the by-bedroom standard placeholder exists for this home, so no number is shown.</div>' : ''}</div>`;
  const leadsN = o.leads.filter(l => isOpenLead(l) && l.property_interest === id).length;
  const more: KebabItem[] = [
    { label: 'Open MTR radar', icon: '📡', attrs: { 'data-mtrc-go': 'vacancy' } },
    { label: 'Open Rates', icon: '📈', attrs: { 'data-mtrc-go': 'rates' } },
    ...CHANNELS.filter(c => listed[c.key] && listed[c.key]!.url).map(c => ({ label: `Open ${c.label} listing`, icon: '↗', attrs: { 'data-mtrc-url': listed[c.key]!.url! } })),
    ...(opts.card ? [{ divider: true } as KebabItem, { label: 'Open the home panel', icon: '🏠', attrs: { 'data-mtrc-open-listing': id } }] : []),
  ];
  return `<div class="mtrc-rates" data-mtrc-rates="${e(id)}">
    <div class="mtrc-line">${mode}${why}</div>
    ${v && v.note ? `<div class="mtrc-note">${e(v.note)}</div>` : ''}
    ${recHtml}
    <div class="mtrc-listed"><div class="mtrc-k">Listed at <span class="mtrc-kh">— what the unit is actually listed for</span></div>
      ${CHANNELS.map(c => listedRow(ctx, id, c, listed[c.key], rec ? rec.rate : null)).join('')}
      <div class="mtrc-err" data-mtrc-err hidden></div>
    </div>
    <div class="mtrc-acts">
      <button type="button" class="btn ${opts.card ? 'btn-primary' : 'btn-secondary'} btn-sm" data-mtrc-newlead="${e(id)}">＋ Lead for this home</button>
      <button type="button" class="btn btn-secondary btn-sm" data-mtrc-newtour="${e(id)}">Schedule tour${leadsN ? ` (${leadsN} lead${leadsN === 1 ? '' : 's'})` : ''}</button>
      ${kebabHtml(more, { tip: 'More MTR actions' })}
    </div>
  </div>`;
}
export function bindRates(root: HTMLElement, id: string, ctx: CalendarCtx, rerender: () => void): void {
  bindKebabs(root);
  const box = root.querySelector<HTMLElement>(`[data-mtrc-rates]`) || root;
  const errBox = box.querySelector<HTMLElement>('[data-mtrc-err]');
  const showErr = (m: string) => { if (errBox) { errBox.textContent = m; errBox.hidden = !m; } };
  const dirtyNow = () => [...box.querySelectorAll<HTMLInputElement>('[data-mtrc-listed]')].some(i => i.value.trim() !== (i.dataset.orig || ''));
  box.querySelectorAll<HTMLInputElement>('[data-mtrc-listed]').forEach(inp => {
    const btn = box.querySelector<HTMLButtonElement>(`[data-mtrc-save="${inp.dataset.mtrcListed}"]`);
    inp.addEventListener('input', () => { if (btn) btn.disabled = inp.value.trim() === (inp.dataset.orig || ''); ctx.markDirty(dirtyNow()); });
    inp.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); btn?.click(); } });
  });
  box.querySelectorAll<HTMLButtonElement>('[data-mtrc-save]').forEach(btn => btn.addEventListener('click', async () => {
    const ch = btn.dataset.mtrcSave!;
    const inp = box.querySelector<HTMLInputElement>(`[data-mtrc-listed="${ch}"]`);
    if (!inp) return;
    const raw = inp.value.trim();
    const n = raw === '' ? null : Number(raw);
    if (n != null && (!Number.isFinite(n) || n < 100 || n >= 100000)) { showErr('Enter the monthly rent the listing shows ($100 – $99,999), or clear it.'); inp.focus(); return; }
    btn.disabled = true; btn.textContent = 'Saving…'; showErr('');
    const r = await api('POST', '/api/mtr/channels', { listing_id: id, channel: ch, listed_rate: n });
    if (!r.ok) { btn.disabled = false; btn.textContent = 'Save'; showErr(errText(r, 'Save')); return; }
    // patch the loaded overlay so the card re-renders right away; the layer reload follows
    const o = ctx.layer<MtrOverlay>('mtr');
    const entry = r.j && r.j.entry ? r.j.entry[ch] : null;
    if (o && o.rates) {
      const cur = o.rates[id] || (o.rates[id] = { recommended: { month: 0, rate: null, source: null, basis: '' }, listed: {} } as any);
      cur.listed = { ...(cur.listed || {}), [ch]: entry ? { url: entry.url || null, verified: !!entry.verified, listed_rate: entry.listed_rate ?? null, listed_rate_at: entry.listed_rate_at || null, listed_rate_by: entry.listed_rate_by || null, scraped_price: entry.scraped_price ?? null, scraped_at: entry.scraped_at || null } : null };
      const vac = (o.vacancies || []).find(x => x.listing_id === id);
      if (vac) vac.listed = cur.listed;
    }
    inp.dataset.orig = n == null ? '' : String(n);
    ctx.markDirty(false);
    ctx.toast(n == null ? `${CHANNELS.find(c => c.key === ch)?.label} listed rate cleared` : `${CHANNELS.find(c => c.key === ch)?.label} listed at ${money(n)}/mo`);
    rerender();
    void ctx.reload({ layers: ['mtr'] });
  }));
  // kebab items are bound per element NOW (while still inside `box`) — the menu moves to <body> on open
  bindAct(box, '[data-mtrc-newlead]', () => { ctx.closePop(); openLeadForm(ctx, { listing_id: id, move_in: defaultMoveIn(ctx, id) }); });
  bindAct(box, '[data-mtrc-newtour]', () => { ctx.closePop(); openTourForm(ctx, { listing_id: id, date: defaultMoveIn(ctx, id) }); });
  bindAct(box, '[data-mtrc-go]', (el) => goMtr(ctx, el.dataset.mtrcGo as any));
  bindAct(box, '[data-mtrc-url]', (el) => { window.open(el.dataset.mtrcUrl!, '_blank', 'noopener'); });
  bindAct(box, '[data-mtrc-open-listing]', (el) => { ctx.closePop(); ctx.openInspector({ kind: 'listing', id: el.dataset.mtrcOpenListing!, listing_id: el.dataset.mtrcOpenListing! }); });
}
function defaultMoveIn(ctx: CalendarCtx, id: string): string {
  const o = ov(ctx);
  const v = o && o.vacancies.find(x => x.listing_id === id);
  const d = v && v.vacant_from && v.vacant_from > ctx.asOf ? v.vacant_from : ctx.asOf;
  return d;
}

// the try-MTR quick card (ChipKind 'mtr_vacancy') — a ≤320px popover, a bottom sheet on phones
export function openVacancyCard(ref: EntityRef, anchor: HTMLElement, ctx: CalendarCtx): void {
  const id = ref.id;
  const o = ov(ctx);
  const v = o && o.vacancies.find(x => x.listing_id === id);
  const e = ctx.esc;
  const inner = () => `<div class="mtrc-pop-head"><span class="mtrc-pop-ic" aria-hidden="true">🏠</span><div class="mtrc-pop-title">${e(ctx.property(id)?.name || (v && v.name) || id)}<div class="mtrc-pop-sub">${e(v ? v.why : 'MTR home')}</div></div><button type="button" class="mtrc-x" data-mtrc-close aria-label="Close">✕</button></div>
      ${ratesHtml(ctx, id, { card: true })}`;
  const root = ctx.openPop(anchor, `<div class="mtrc-pop" data-mtrc-pop>${inner()}</div>`, { width: 320, cls: 'mtrc-popwrap', onClose: () => { st.dirty = false; ctx.markDirty(false); } });
  const host = () => (root.matches('[data-mtrc-pop]') ? root : root.querySelector<HTMLElement>('[data-mtrc-pop]')) || root;
  const draw = () => { host().innerHTML = inner(); wire(); };
  const wire = () => {
    bindRates(host(), id, ctx, draw);
    bindAct(host(), '[data-mtrc-close]', () => ctx.closePop());
  };
  wire();
}

// ══ stay / tenant → "MTR tenant" ═════════════════════════════════════════
// R116 #4 (Kai): + a Notes row = the COUNT and the open follow-up pill ("⏰ check Oct 15–17 · now") — the note text
// itself never rides the 15-min overlay; "Rent & notes" opens the shared panel (GET /api/mtr/tenants/:id).
export function tenantHtml(ctx: CalendarCtx, ts: MtrTenant[]): string {
  const e = ctx.esc;
  return ts.map(t => {
    const p = t.payment;
    const lease = `${t.lease_start ? e(ctx.fmt.md(t.lease_start)) : '?'} → ${t.lease_end ? e(ctx.fmt.md(t.lease_end)) : 'open-ended'}`;
    const rent = t.monthly_amount != null ? `<b>${money(t.monthly_amount)}</b>/mo · due day ${e(t.due_day || 1)}` : '<span class="mtrc-muted">no monthly rent set</span>';
    const chip = `<span class="mtrc-pill mtrc-pill-${e(p.chip.tone)}" title="${e(p.chip.title)}">${e(p.chip.label)}</span>`;
    const fu = t.follow_up || null;
    const fuTone = fu ? (fu.tone === 'overdue' ? 'bad' : fu.tone === 'now' ? 'warn' : 'muted') : '';
    const fuPill = fu ? `<span class="mtrc-pill mtrc-pill-${fuTone}" title="open follow-up — check it off in Rent &amp; notes">⏰ ${e(fu.label)}${fu.tone === 'overdue' ? ' · overdue' : fu.tone === 'now' ? ' · now' : ''}</span>` : '';
    const nN = Number(t.notes_count) || 0;
    const notesRow = `<div class="mtrc-kv-r"><span>Notes</span><span class="mtrc-notes-v">${nN ? `<b>${nN}</b>` : '<span class="mtrc-muted">none yet</span>'}${fuPill ? ` ${fuPill}` : ''}</span></div>`;
    const record = p.on_time || p.late ? `${p.on_time}✓${p.late ? ` · ${p.late} late` : ''} of ${p.logged} logged` : (p.logged ? `${p.logged} logged` : '—');
    const wos = t.open_wos.items.map(w => `<button type="button" class="mtrc-wo" data-mtrc-wo="${e(w.id)}" data-listing="${e(t.listing_id)}">
        <span class="mtrc-wo-ic mtrc-wo-${w.priority === 'emergency' || w.priority === 'urgent' ? 'hot' : 'routine'}" aria-hidden="true">🔧</span>
        <span class="mtrc-wo-t">${e(w.title)}</span><span class="mtrc-wo-s">${e(String(w.status).replace(/_/g, ' '))}${w.date ? ` · ${e(ctx.fmt.md(w.date))}` : ''}</span></button>`).join('');
    return `<div class="mtrc-ten" data-mtrc-ten="${e(t.tenant_id)}">
      <div class="mtrc-ten-head"><b>${e(t.name)}</b><span class="mtrc-pill">${t.source === 'auto' ? 'from the booking' : 'direct lease'}</span>${chip}</div>
      <div class="mtrc-kv">
        <div class="mtrc-kv-r"><span>Rent</span><span>${rent}</span></div>
        <div class="mtrc-kv-r"><span>Rent status</span><span>${e(p.chip.label)}${p.next_due ? ` · next due ${e(ctx.fmt.md(p.next_due))}` : ''}</span></div>
        <div class="mtrc-kv-r"><span>On-time record</span><span>${e(record)}</span></div>
        <div class="mtrc-kv-r"><span>Lease</span><span>${lease}</span></div>
        ${notesRow}
      </div>
      ${t.open_wos.count ? `<div class="mtrc-sub">Open work orders during the lease · ${t.open_wos.count}</div><div class="mtrc-wos">${wos}</div>` : '<div class="mtrc-muted mtrc-sub">No open work orders during this lease.</div>'}
      <div class="mtrc-acts">
        ${t.unsaved ? '<span class="mtrc-muted">Detected from the booking — open MTR → Tenants once to start tracking rent and notes.</span>' : `<button type="button" class="btn btn-secondary btn-sm" data-mtrc-ledger="${e(t.tenant_id)}" title="${e(`${p.logged} payment record${p.logged === 1 ? '' : 's'} · ${nN} note${nN === 1 ? '' : 's'}`)}">Rent &amp; notes</button>`}
        <button type="button" class="btn btn-ghost btn-sm" data-mtrc-go="tenants">Open in MTR → Tenants</button>
      </div>
    </div>`;
  }).join('');
}
export function bindTenant(root: HTMLElement, ctx: CalendarCtx): void {
  bindAct(root, '[data-mtrc-ledger]', (el) => {
    const id = el.dataset.mtrcLedger!;
    const o = ov(ctx);
    const t = o && o.tenants.find(x => x.tenant_id === id);
    openLedger(ctx, id, t ? t.name : 'Tenant');
  });
  bindAct(root, '[data-mtrc-wo]', (el) => ctx.openInspector({ kind: 'wo', id: el.dataset.mtrcWo!, listing_id: el.dataset.listing }));
  bindAct(root, '[data-mtrc-go]', (el) => goMtr(ctx, el.dataset.mtrcGo as any));
}
export function tenantsForRef(ctx: CalendarCtx, ref: EntityRef, data: any): MtrTenant[] {
  const o = ov(ctx);
  if (!o) return [];
  if (ref.kind === 'tenant') { const t = o.tenants.find(x => x.tenant_id === ref.id) || (data && data.tenant_id ? data : null); return t ? [t] : []; }
  const stay = ctx.data.stays.find(s => String(s.id) === String(ref.id)) || (data && data.stay) || null;
  return stay ? tenantsForStay(o, stay) : [];
}
/** The shared tenant panel (rent + notes + follow-ups) in the form sheet. R116: `focus` opens it on the note composer;
 *  typed text marks the sheet dirty (the page asks "Discard what you typed?" and vetoes the 15-min refresh). */
export function openLedger(ctx: CalendarCtx, tenantId: string, name: string, focus?: 'note' | 'pay'): void {
  const sheet = ctx.openSheet(`<div class="mtrc-ledger" data-mtrc-ledger-host><div class="mtrc-muted">Loading rent &amp; notes…</div></div>`, { title: `${name} — rent & notes`, onClose: () => { st.tenantCache.delete(tenantId); st.dirty = false; ctx.markDirty(false); } });
  const host = sheet.querySelector<HTMLElement>('[data-mtrc-ledger-host]') || sheet;
  const fetchT = async () => {
    const r = await api('GET', `/api/mtr/tenants/${encodeURIComponent(tenantId)}`);
    if (!r.ok) throw new Error(errText(r, 'Loading the tenant'));
    st.tenantCache.set(tenantId, r.j);
  };
  void loadPeople();
  fetchT().then(() => {
    mountRentLedger(host, {
      tenantId,
      getTenant: () => st.tenantCache.get(tenantId) || null,
      refresh: fetchT,
      applyTenant: (v) => { st.tenantCache.set(tenantId, v); },
      onChanged: () => { void ctx.reload({ layers: ['mtr'], inspector: true }); },
      onDirty: (d) => { st.dirty = d; ctx.markDirty(d); },
      whoName: (by) => whoFrom(st.people, by),
      focus,
    });
  }).catch((err) => { host.innerHTML = `<div class="mtrc-err">${ctx.esc(err && err.message || 'Could not load the tenant.')}</div>`; });
}

// ══ lead inspector sections ═══════════════════════════════════════════════
export function leadPipelineHtml(ctx: CalendarCtx, l: MtrLead): string {
  const e = ctx.esc;
  const stages = stagesOf(ov(ctx));
  const i = stages.indexOf(l.stage);
  const next = i >= 0 && i < stages.indexOf('active') ? stages[i + 1] : null;
  const steps = stages.filter(s => !TERMINAL.includes(s)).map(s => `<span class="mtrc-step${s === l.stage ? ' on' : stages.indexOf(s) < i && !TERMINAL.includes(l.stage) ? ' done' : ''}">${e(stageLabel(s))}</span>`).join('');
  return `<div class="mtrc-pipe" data-mtrc-pipe="${e(l.id)}">
    <div class="mtrc-stage-row">
      <label class="mtrc-k" for="mtrc-stage-${e(l.id)}">Stage</label>
      <select id="mtrc-stage-${e(l.id)}" data-mtrc-stage>${stages.map(s => `<option value="${e(s)}"${s === l.stage ? ' selected' : ''}>${e(stageLabel(s))}</option>`).join('')}</select>
      ${next ? `<button type="button" class="btn btn-secondary btn-sm" data-mtrc-next="${e(next)}">→ ${e(stageLabel(next))}</button>` : ''}
      ${TERMINAL.includes(l.stage) ? `<button type="button" class="btn btn-secondary btn-sm" data-mtrc-next="${e(l.reopen_to || 'contacted')}">↺ Reopen</button>` : ''}
    </div>
    <div class="mtrc-steps" aria-hidden="true">${steps}</div>
    <div data-mtrc-ask></div>
    <div class="mtrc-err" data-mtrc-err hidden></div>
  </div>`;
}
export function bindLeadPipeline(root: HTMLElement, ctx: CalendarCtx, l: MtrLead): void {
  const box = root.querySelector<HTMLElement>('[data-mtrc-pipe]') || root;
  const sel = box.querySelector<HTMLSelectElement>('[data-mtrc-stage]');
  const ask = box.querySelector<HTMLElement>('[data-mtrc-ask]');
  const err = box.querySelector<HTMLElement>('[data-mtrc-err]');
  const want = (to: string) => {
    if (!to || to === l.stage) return;
    if (TERMINAL.includes(to)) { if (sel) sel.value = l.stage; openReasonForm(ctx, l, to); return; }
    const back = SETTLED.includes(l.stage) && stagesOf(ov(ctx)).indexOf(to) < stagesOf(ov(ctx)).indexOf(l.stage);
    const reopen = TERMINAL.includes(l.stage);
    if ((back || reopen) && ask) {
      ask.innerHTML = `<div class="mtrc-ask">${reopen ? `Reopen ${ctx.esc(l.name)} as ${ctx.esc(stageLabel(to))}?` : `Move ${ctx.esc(l.name)} back from ${ctx.esc(stageLabel(l.stage))} to ${ctx.esc(stageLabel(to))}?`}
        <span class="mtrc-ask-acts"><button type="button" class="btn btn-primary btn-sm" data-mtrc-ask-yes>Move</button><button type="button" class="btn btn-ghost btn-sm" data-mtrc-ask-no>Cancel</button></span></div>`;
      ask.querySelector('[data-mtrc-ask-yes]')?.addEventListener('click', () => { ask.innerHTML = ''; void moveLead(ctx, l, to, null, err); });
      ask.querySelector('[data-mtrc-ask-no]')?.addEventListener('click', () => { ask.innerHTML = ''; if (sel) sel.value = l.stage; });
      return;
    }
    void moveLead(ctx, l, to, null, err);
  };
  sel?.addEventListener('change', () => want(sel.value));
  bindAct(box, '[data-mtrc-next]', (el) => want(el.dataset.mtrcNext!));
}
/** POST /leads/:id {stage, expect_stage[, note]} → toast with Undo; 409 stale → refreshed. */
export async function moveLead(ctx: CalendarCtx, l: MtrLead, to: string, note: string | null, errBox?: HTMLElement | null): Promise<boolean> {
  return (await moveLeadStatus(ctx, l, to, note, errBox)) === 'ok';
}
/** R115 review: the same move, telling the caller WHY it didn't land ('stale' = someone moved the lead; the fresh copy
 *  is in st.leadCache) so a reason form keeps what was typed. */
export async function moveLeadStatus(ctx: CalendarCtx, l: MtrLead, to: string, note: string | null, errBox?: HTMLElement | null, quiet409 = false): Promise<'ok' | 'stale' | 'error'> {
  const from = l.stage;
  const body: Record<string, unknown> = { stage: to, expect_stage: from };
  if (note && note.trim()) body.note = note.trim();
  const r = await api('POST', `/api/mtr/leads/${encodeURIComponent(l.id)}`, body);
  if (r.status === 409) {
    const cur = safeLead(r.j && r.j.lead);
    if (cur) st.leadCache.set(cur.id, cur);
    if (!quiet409) ctx.toast('Someone else moved this lead — refreshed.');
    await reloadMtr(ctx);
    return 'stale';
  }
  if (!r.ok) {
    if (errBox) { errBox.textContent = errText(r, 'Moving the lead'); errBox.hidden = false; } else ctx.toast(errText(r, 'Moving the lead'));
    return 'error';
  }
  const v = safeLead(r.j);
  if (v) st.leadCache.set(v.id, v);
  ctx.toastUndo(`${firstName(l.name)} → ${stageLabel(to)}`, () => {
    void api('POST', `/api/mtr/leads/${encodeURIComponent(l.id)}`, { stage: from, expect_stage: to }).then(u => {
      if (u.ok) { const b = safeLead(u.j); if (b) st.leadCache.set(b.id, b); }
      else ctx.toast(u.status === 409 ? 'The lead moved again since — not undone.' : errText(u, 'Undo'));
      void reloadMtr(ctx);
    });
  });
  await reloadMtr(ctx);
  return 'ok';
}

export function leadToursHtml(ctx: CalendarCtx, l: MtrLead): string {
  const e = ctx.esc;
  const rows = (l.tours || []).slice().sort((a, b) => (a.at < b.at ? -1 : 1)).map(t => tourRowHtml(ctx, l, t)).join('');
  return `<div class="mtrc-tours" data-mtrc-tours="${e(l.id)}">
    ${rows || '<div class="mtrc-muted">No tours yet.</div>'}
    <div class="mtrc-err" data-mtrc-err hidden></div>
    <div class="mtrc-acts"><button type="button" class="btn btn-secondary btn-sm" data-mtrc-addtour="${e(l.id)}">＋ Schedule tour</button></div>
  </div>`;
}
function tourRowHtml(ctx: CalendarCtx, l: MtrLead, t: MtrLeadTour): string {
  const e = ctx.esc;
  const w = wall(t.at);
  const when = w ? `${ctx.fmt.wmd(w.date)} · ${timeLabel(t.at)}` : t.at;
  const sch = t.status === 'scheduled';
  const more: KebabItem[] = [
    { label: 'Reschedule…', icon: '🗓', attrs: { 'data-mtrc-tour-move': t.id, 'data-lead': l.id } },
    ...(sch ? [{ label: 'Cancel tour', icon: '⊘', attrs: { 'data-mtrc-tour-st': 'cancelled', 'data-tour': t.id, 'data-lead': l.id } }] : [{ label: 'Mark scheduled again', icon: '↺', attrs: { 'data-mtrc-tour-st': 'scheduled', 'data-tour': t.id, 'data-lead': l.id } }]),
    { divider: true },
    { label: 'Delete tour', icon: '✕', danger: true, attrs: { 'data-mtrc-tour-del': t.id, 'data-lead': l.id } },
  ];
  return `<div class="mtrc-tour mtrc-tour-${e(t.status)}" data-mtrc-tour="${e(t.id)}">
    <div class="mtrc-tour-main"><div class="mtrc-tour-when">${e(when)} <span class="mtrc-pill mtrc-pill-${t.status === 'done' ? 'ok' : sch ? 'info' : 'muted'}">${e(TOUR_LABEL[t.status] || t.status)}</span></div>
      ${t.with || t.note ? `<div class="mtrc-tour-sub">${t.with ? `with ${e(t.with)}` : ''}${t.with && t.note ? ' · ' : ''}${t.note ? e(t.note) : ''}</div>` : ''}</div>
    <div class="mtrc-tour-acts">
      ${sch ? `<button type="button" class="btn btn-secondary btn-sm" data-mtrc-tour-st="done" data-tour="${e(t.id)}" data-lead="${e(l.id)}">Done</button><button type="button" class="btn btn-ghost btn-sm" data-mtrc-tour-st="no_show" data-tour="${e(t.id)}" data-lead="${e(l.id)}">No-show</button>` : ''}
      ${kebabHtml(more, { tip: 'Tour actions' })}
    </div>
    <div data-mtrc-tour-ask="${e(t.id)}"></div>
  </div>`;
}
export function bindTours(root: HTMLElement, ctx: CalendarCtx, l: MtrLead): void {
  bindKebabs(root);
  const err = root.querySelector<HTMLElement>('[data-mtrc-tours] [data-mtrc-err]');
  // kebab items are bound per element while still inside `root` (the menu moves to <body> on open)
  for (const sc of [root]) {
    bindAct(sc, '[data-mtrc-tour-st]', (el) => { void setTourStatus(ctx, el.dataset.lead!, el.dataset.tour!, el.dataset.mtrcTourSt!, err); });
    bindAct(sc, '[data-mtrc-tour-move]', (el) => { const t = (leadOf(ctx, el.dataset.lead!)?.tours || []).find(x => x.id === el.dataset.mtrcTourMove); if (t) openRescheduleForm(ctx, el.dataset.lead!, t); });
    bindAct(sc, '[data-mtrc-tour-del]', (el) => {
      const slot = root.querySelector<HTMLElement>(`[data-mtrc-tour-ask="${el.dataset.mtrcTourDel}"]`);
      if (!slot) return;
      slot.innerHTML = `<div class="mtrc-ask">Delete this tour? History keeps a "tour deleted" line.<span class="mtrc-ask-acts"><button type="button" class="btn btn-danger btn-sm" data-yes>Delete</button><button type="button" class="btn btn-ghost btn-sm" data-no>Keep</button></span></div>`;
      slot.querySelector('[data-yes]')?.addEventListener('click', async () => {
        slot.innerHTML = '';
        const r = await api('DELETE', `/api/mtr/leads/${encodeURIComponent(el.dataset.lead!)}/tours/${encodeURIComponent(el.dataset.mtrcTourDel!)}`);
        if (!r.ok) { if (err) { err.textContent = errText(r, 'Deleting the tour'); err.hidden = false; } return; }
        const v = safeLead(r.j && r.j.lead); if (v) st.leadCache.set(v.id, v);
        ctx.toast('Tour deleted');
        await reloadMtr(ctx);
      });
      slot.querySelector('[data-no]')?.addEventListener('click', () => { slot.innerHTML = ''; });
    });
  }
  bindAct(root, '[data-mtrc-addtour]', () => openTourForm(ctx, { listing_id: l.property_interest, date: ctx.asOf, lead_id: l.id }));
}
export async function setTourStatus(ctx: CalendarCtx, leadId: string, tourId: string, status: string, errBox?: HTMLElement | null): Promise<boolean> {
  const r = await api('PUT', `/api/mtr/leads/${encodeURIComponent(leadId)}/tours/${encodeURIComponent(tourId)}`, { status });
  if (!r.ok) { if (errBox) { errBox.textContent = errText(r, 'Updating the tour'); errBox.hidden = false; } else ctx.toast(errText(r, 'Updating the tour')); return false; }
  const v = safeLead(r.j && r.j.lead); if (v) st.leadCache.set(v.id, v);
  ctx.toast(`Tour marked ${TOUR_LABEL[status] || status}`);
  await reloadMtr(ctx);
  return true;
}

export function leadDetailsHtml(ctx: CalendarCtx, l: MtrLead): string {
  const e = ctx.esc;
  const row = (k: string, v: string) => `<div class="mtrc-kv-r"><span>${e(k)}</span><span>${v}</span></div>`;
  return `<div class="mtrc-kv">
    ${row('Home', l.property_interest ? `<button type="button" class="mtrc-link" data-mtrc-open-listing="${e(l.property_interest)}">${e(homeName(ctx, l.property_interest))}</button>` : '<span class="mtrc-muted">not chosen yet</span>')}
    ${row('Move-in', l.move_in ? e(ctx.fmt.wmd(l.move_in)) : '<span class="mtrc-muted">—</span>')}
    ${row('Term', l.term_months ? `${e(l.term_months)} months` : '<span class="mtrc-muted">—</span>')}
    ${row('Offer', l.offer_price ? `${money(l.offer_price)}/mo` : '<span class="mtrc-muted">—</span>')}
    ${row('In this stage since', l.stage_changed_at ? e(ctx.fmt.md(String(l.stage_changed_at).slice(0, 10))) : '<span class="mtrc-muted">—</span>')}
  </div>
  <div class="mtrc-acts"><button type="button" class="btn btn-ghost btn-sm" data-mtrc-pipeline="${e(l.id)}">Open card in Pipeline ↗</button></div>
  <div class="mtrc-muted mtrc-small">Contact details, checks, notes and history live on the Pipeline card.</div>`;
}
export function bindLeadDetails(root: HTMLElement, ctx: CalendarCtx): void {
  bindAct(root, '[data-mtrc-open-listing]', (el) => ctx.openInspector({ kind: 'listing', id: el.dataset.mtrcOpenListing!, listing_id: el.dataset.mtrcOpenListing! }));
  bindAct(root, '[data-mtrc-pipeline]', (el) => goMtr(ctx, 'leads', el.dataset.mtrcPipeline!));
}

// the tour quick card (ChipKind 'tour') — one-tap Done / No-show, the rest in ⋯
export function openTourCard(ref: EntityRef, anchor: HTMLElement, ctx: CalendarCtx): void {
  const o = ov(ctx);
  const t: MtrTour | undefined = o ? o.tours.find(x => x.id === ref.id) : undefined;
  if (!t) { ctx.toast('That tour is no longer on the calendar — refreshing.'); void reloadMtr(ctx); return; }
  const e = ctx.esc;
  const lead = leadOf(ctx, t.lead_id);
  const sch = t.status === 'scheduled';
  const more: KebabItem[] = [
    { label: 'Reschedule…', icon: '🗓', attrs: { 'data-mtrc-card-move': '1' } },
    sch ? { label: 'Cancel tour', icon: '⊘', attrs: { 'data-mtrc-card-st': 'cancelled' } } : { label: 'Mark scheduled again', icon: '↺', attrs: { 'data-mtrc-card-st': 'scheduled' } },
    { label: 'Open the lead', icon: '👤', attrs: { 'data-mtrc-card-lead': '1' } },
  ];
  const html = `<div class="mtrc-pop" data-mtrc-pop>
    <div class="mtrc-pop-head"><span class="mtrc-pop-ic" aria-hidden="true">🗓</span><div class="mtrc-pop-title">Tour · ${e(t.lead_name)}<div class="mtrc-pop-sub">${e(ctx.fmt.wmd(t.date))} · ${e(timeLabel(t.at))} · ${e(homeName(ctx, t.listing_id))}</div></div><button type="button" class="mtrc-x" data-mtrc-close aria-label="Close">✕</button></div>
    <div class="mtrc-kv">
      <div class="mtrc-kv-r"><span>Status</span><span><span class="mtrc-pill mtrc-pill-${t.status === 'done' ? 'ok' : sch ? 'info' : 'muted'}">${e(TOUR_LABEL[t.status] || t.status)}</span></span></div>
      ${lead ? `<div class="mtrc-kv-r"><span>Lead stage</span><span>${e(stageLabel(lead.stage))}</span></div>` : ''}
      ${t.with ? `<div class="mtrc-kv-r"><span>With</span><span>${e(t.with)}</span></div>` : ''}
      ${t.note ? `<div class="mtrc-kv-r"><span>Note</span><span>${e(t.note)}</span></div>` : ''}
    </div>
    <div class="mtrc-err" data-mtrc-err hidden></div>
    <div class="mtrc-acts">
      ${sch ? '<button type="button" class="btn btn-primary btn-sm" data-mtrc-card-st="done">Done</button><button type="button" class="btn btn-secondary btn-sm" data-mtrc-card-st="no_show">No-show</button>' : '<button type="button" class="btn btn-primary btn-sm" data-mtrc-card-lead="1">Open the lead</button>'}
      ${kebabHtml(more, { tip: 'Tour actions' })}
    </div>
  </div>`;
  const root = ctx.openPop(anchor, html, { width: 300, cls: 'mtrc-popwrap' });
  bindKebabs(root);
  const err = root.querySelector<HTMLElement>('[data-mtrc-err]');
  bindAct(root, '[data-mtrc-close]', () => ctx.closePop());
  for (const sc of [root]) {
    bindAct(sc, '[data-mtrc-card-st]', async (el) => { if (await setTourStatus(ctx, t.lead_id, t.id, el.dataset.mtrcCardSt!, err)) ctx.closePop(); });
    bindAct(sc, '[data-mtrc-card-lead]', () => { ctx.closePop(); ctx.openInspector({ kind: 'lead', id: t.lead_id, listing_id: t.listing_id || undefined }); });
    bindAct(sc, '[data-mtrc-card-move]', () => { ctx.closePop(); openRescheduleForm(ctx, t.lead_id, { id: t.id, at: t.at, duration_min: t.duration_min, status: t.status, with: t.with, note: t.note }); });
  }
}
/** ChipKind 'tour' move: keep the wall-clock time, change the day. */
export async function moveTourTo(ctx: CalendarCtx, tourId: string, date: string): Promise<void> {
  const o = ov(ctx);
  const t = o && o.tours.find(x => x.id === tourId);
  if (!t) return;
  const w = wall(t.at);
  const at = `${date}T${w ? `${String(w.h).padStart(2, '0')}:${String(w.mi).padStart(2, '0')}` : '11:00'}`;
  const r = await api('PUT', `/api/mtr/leads/${encodeURIComponent(t.lead_id)}/tours/${encodeURIComponent(t.id)}`, { at });
  if (!r.ok) { ctx.toast(errText(r, 'Moving the tour')); return; }
  ctx.toast(`Tour moved to ${ctx.fmt.wmd(date)} ${timeLabel(at)}${tourWarn(r)}`);
  await reloadMtr(ctx);
}
/** R116 #7: the server checks a hand-picked tour day (a guest inside, before the clean, after the move-in…) — say so, never block. */
function tourWarn(r: ApiRes): string {
  const w = r && r.j && Array.isArray(r.j.warnings) ? r.j.warnings.find((x: any) => x && x.code !== 'past') : null;
  return w && w.text ? ` · ⚠ ${String(w.text)}` : '';
}

// ══ form sheets ═══════════════════════════════════════════════════════════
function sheetForm(ctx: CalendarCtx, title: string, body: string, submitLabel: string, onSubmit: (form: HTMLFormElement, setErr: (m: string) => void) => Promise<boolean>): HTMLFormElement | null {
  const host = ctx.openSheet(`<form class="mtrc-form" novalidate>
      <div class="mtrc-fbody">${body}<div class="mtrc-err" data-mtrc-err hidden></div></div>
      <div class="mtrc-foot" data-bottom-bar><button type="button" class="btn btn-secondary" data-mtrc-cancel>Cancel</button><button type="submit" class="btn btn-primary" data-mtrc-submit>${ctx.esc(submitLabel)}</button></div>
    </form>`, { title, onClose: () => { st.dirty = false; ctx.markDirty(false); } });
  const form = (host.matches('form') ? host : host.querySelector('form')) as HTMLFormElement | null;
  if (!form) return null;
  const errBox = form.querySelector<HTMLElement>('[data-mtrc-err]');
  const setErr = (m: string) => { if (errBox) { errBox.textContent = m; errBox.hidden = !m; } };
  form.addEventListener('input', () => { st.dirty = true; ctx.markDirty(true); });
  form.querySelector('[data-mtrc-cancel]')?.addEventListener('click', () => { st.dirty = false; ctx.markDirty(false); ctx.closeSheet(); });
  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const btn = form.querySelector<HTMLButtonElement>('[data-mtrc-submit]');
    if (btn) { if (btn.disabled) return; btn.disabled = true; }
    setErr('');
    let ok = false;
    try { ok = await onSubmit(form, setErr); } catch (e: any) { setErr(e && e.message ? e.message : 'Something went wrong — nothing was saved.'); }
    if (ok) { st.dirty = false; ctx.markDirty(false); ctx.closeSheet(); }
    else if (btn) btn.disabled = false;
  });
  setTimeout(() => form.querySelector<HTMLElement>('[data-autofocus]')?.focus(), 30);
  return form;
}
const val = (f: HTMLFormElement, name: string) => String((f.elements.namedItem(name) as HTMLInputElement | null)?.value ?? '').trim();
function homeOptions(ctx: CalendarCtx, selected: string | null): string {
  const o = ov(ctx);
  // The MTR view lists its MTR homes; every other view offers every home on the calendar (R115 integ D2 gap) — even
  // after a form fetched the mtr layer for its lead list.
  const ids = new Set<string>(ctx.view === 'mtr' && o && o.homes.length ? o.homes : ctx.allProperties().map(p => p.listing_id));
  if (selected) ids.add(selected);
  const opts = [...ids].map(id => ({ id, name: homeName(ctx, id) })).sort((a, b) => a.name.localeCompare(b.name));
  return `<option value="">— not decided —</option>${opts.map(h => `<option value="${ctx.esc(h.id)}"${h.id === selected ? ' selected' : ''}>${ctx.esc(h.name)}</option>`).join('')}`;
}

/** + Lead (date action 'lead', the try-MTR card, the listing section). */
export function openLeadForm(ctx: CalendarCtx, o: { listing_id: string | null; move_in?: string | null }): void {
  const e = ctx.esc;
  sheetForm(ctx, 'New MTR lead', `
    <label class="mtrc-f">Renter name<input name="name" autocomplete="off" required data-autofocus placeholder="name or company"></label>
    <label class="mtrc-f">Phone or email<input name="contact" autocomplete="off" placeholder="optional — stays on the Pipeline card"></label>
    <label class="mtrc-f">${o.listing_id ? 'Home' : 'Which home?'}<select name="property_interest">${homeOptions(ctx, o.listing_id)}</select></label>
    <div class="mtrc-f2">
      <label class="mtrc-f">Move-in<input type="date" name="move_in" value="${e(o.move_in || '')}"></label>
      <label class="mtrc-f">Term (months)<input type="number" inputmode="numeric" min="1" max="36" name="term_months" placeholder="e.g. 3"></label>
    </div>
    <label class="mtrc-f">Their offer $/mo<input type="number" inputmode="decimal" min="0" name="offer_price" placeholder="optional"></label>`,
  'Add lead', async (f, setErr) => {
    const name = val(f, 'name');
    if (!name) { setErr('Add the renter’s name.'); return false; }
    const r = await api('POST', '/api/mtr/leads', {
      name, contact: val(f, 'contact') || null, property_interest: val(f, 'property_interest') || null, move_in: val(f, 'move_in') || null,
      term_months: val(f, 'term_months') || null, offer_price: val(f, 'offer_price') || null,
    });
    if (!r.ok) { setErr(errText(r, 'Adding the lead')); return false; }
    const v = safeLead(r.j);
    if (v) st.leadCache.set(v.id, v);
    ctx.toast(`Lead added — ${name}`);
    await reloadMtr(ctx);
    if (v) ctx.openInspector({ kind: 'lead', id: v.id, listing_id: v.property_interest || undefined });
    return true;
  });
}

/** Schedule a tour (date action 'tour', the lead inspector, the try-MTR card). R115 review: outside the MTR view the
 *  lead list is fetched first (ctx.ensureLayer — never only "＋ New lead", which made duplicate Pipeline cards); the
 *  tour keeps the home picked here (a renter may tour two homes); a retry after the tour POST failed reuses the lead
 *  it already created. */
export function openTourForm(ctx: CalendarCtx, o: { listing_id: string | null; date: string; lead_id?: string }): void {
  const e = ctx.esc;
  let loaded = !!ov(ctx);
  let created: { id: string; name: string } | null = null;   // a '＋ New lead' that already landed
  const leadsNow = () => {
    const ovl = ov(ctx);
    const open = (ovl ? ovl.leads : []).filter(l => !TERMINAL.includes(l.stage));
    if (o.lead_id && !open.some(l => l.id === o.lead_id)) { const l = leadOf(ctx, o.lead_id); if (l) open.push(l); }
    if (created && !open.some(l => l.id === created!.id)) open.push({ id: created.id, name: created.name, stage: 'new', property_interest: o.listing_id, move_in: null, term_months: null, offer_price: null, stage_changed_at: null, created_at: null, updated_at: null, tours_count: 0, next_tour: null, tours: [] });
    return open;
  };
  const leadOptions = (keep: string): string => {
    if (!loaded) return '<option value="" selected disabled>Loading leads…</option>';
    const open = leadsNow();
    const here = open.filter(l => o.listing_id && l.property_interest === o.listing_id);
    const other = open.filter(l => !here.includes(l));
    const pre = keep || (created ? created.id : '') || o.lead_id || (here.length === 1 ? here[0].id : '');
    const opt = (l: MtrLead) => `<option value="${e(l.id)}" data-stage="${e(l.stage)}"${l.id === pre ? ' selected' : ''}>${e(l.name)} · ${e(stageLabel(l.stage))}${l.property_interest && l.property_interest !== o.listing_id ? ` · ${e(homeName(ctx, l.property_interest))}` : ''}</option>`;
    return `${!pre && open.length ? '<option value="" selected disabled>Pick a lead…</option>' : ''}
      ${here.length ? `<optgroup label="For this home">${here.map(opt).join('')}</optgroup>` : ''}
      ${other.length ? `<optgroup label="Other open leads">${other.map(opt).join('')}</optgroup>` : ''}
      <option value="__new"${!open.length && !pre ? ' selected' : ''}>＋ New lead…</option>`;
  };
  const people = () => (st.people || []).map(p => `<option value="${e(p.full_name.includes('@') ? p.full_name.split('@')[0] : p.full_name)}"></option>`).join('');
  const form = sheetForm(ctx, 'Schedule an MTR tour', `
    <label class="mtrc-f">Lead<select name="lead_id" data-mtrc-leadsel>${leadOptions('')}</select></label>
    <label class="mtrc-f" data-mtrc-newname hidden>New renter’s name<input name="new_name" autocomplete="off" placeholder="name or company"></label>
    <label class="mtrc-f">${o.listing_id ? 'Home' : 'Which home?'}<select name="listing_id">${homeOptions(ctx, o.listing_id)}</select></label>
    <label class="mtrc-f">When<input type="datetime-local" name="at" value="${e(`${o.date}T11:00`)}" step="900" data-autofocus></label>
    <label class="mtrc-f">With (who shows it)<input name="with" list="mtrc-people" autocomplete="off" placeholder="optional"><datalist id="mtrc-people">${people()}</datalist></label>
    <label class="mtrc-f">Note<input name="note" autocomplete="off" placeholder="optional — gate code, parking…"></label>
    <label class="mtrc-check" data-mtrc-advance><input type="checkbox" name="advance" checked> Move the lead to <b>Touring</b> (only from New / Contacted / Qualified)</label>`,
  'Schedule tour', async (f, setErr) => {
    if (!loaded) { setErr('Still loading the leads — one moment.'); return false; }
    let leadId = val(f, 'lead_id');
    const listing = val(f, 'listing_id') || null;
    const at = val(f, 'at');
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(at)) { setErr('Pick the tour day and time.'); return false; }
    if (at.slice(0, 10) < ctx.asOf) { setErr('That day has passed — pick today or later.'); return false; }
    let expect: string | null = null;
    if (!leadId) { setErr('Pick the lead (or ＋ New lead).'); return false; }
    if (leadId === '__new' && created) leadId = created.id;   // a retry: the lead already exists
    if (leadId === '__new') {
      const name = val(f, 'new_name');
      if (!name) { setErr('Add the new renter’s name.'); return false; }
      const dup = leadsNow().find(l => l.name.trim().toLowerCase() === name.toLowerCase());
      if (dup) { setErr(`${dup.name} is already an open lead (${stageLabel(dup.stage)}) — pick them in the Lead list instead of adding a second card.`); return false; }
      const c = await api('POST', '/api/mtr/leads', { name, property_interest: listing, move_in: listing ? defaultMoveIn(ctx, listing) : null });
      if (!c.ok) { setErr(errText(c, 'Adding the lead')); return false; }
      leadId = String(c.j.id);
      created = { id: leadId, name };
      expect = 'new';
      // the form now points at the created lead, so a retry can never make a second one
      const ls = f.querySelector<HTMLSelectElement>('[data-mtrc-leadsel]');
      if (ls) { ls.innerHTML = leadOptions(leadId); ls.value = leadId; }
      const nn = f.querySelector<HTMLElement>('[data-mtrc-newname]'); if (nn) nn.hidden = true;
    } else {
      const l = leadOf(ctx, leadId);
      expect = l ? l.stage : (created && created.id === leadId ? 'new' : null);
      // a lead with no home yet takes this one
      if (l && !l.property_interest && listing) await api('POST', `/api/mtr/leads/${encodeURIComponent(leadId)}`, { property_interest: listing });
    }
    const adv = (f.elements.namedItem('advance') as HTMLInputElement | null)?.checked !== false;
    const r = await api('POST', `/api/mtr/leads/${encodeURIComponent(leadId)}/tours`, { at, with: val(f, 'with') || null, note: val(f, 'note') || null, advance: adv, expect_stage: expect, listing_id: listing });
    if (r.status === 409) { setErr('Someone else moved this lead meanwhile — the calendar was refreshed; check its stage and schedule again.'); void reloadMtr(ctx); return false; }
    if (!r.ok) { setErr(`${created && created.id === leadId ? `${created.name} was added to the Pipeline, but the tour didn't save — ` : ''}${errText(r, 'Scheduling the tour')}`); return false; }
    const v = safeLead(r.j && r.j.lead);
    if (v) st.leadCache.set(v.id, v);
    ctx.toast(`Tour set · ${ctx.fmt.wmd(at.slice(0, 10))} ${timeLabel(at)}${r.j && r.j.advanced ? ' · lead → Touring' : ''}${tourWarn(r)}`);
    await reloadMtr(ctx);
    return true;
  });
  if (!form) return;
  const sel = form.querySelector<HTMLSelectElement>('[data-mtrc-leadsel]');
  const sync = () => {
    const isNew = sel?.value === '__new';
    const nn = form.querySelector<HTMLElement>('[data-mtrc-newname]');
    if (nn) nn.hidden = !isNew;
    const stage = isNew ? 'new' : sel?.selectedOptions[0]?.dataset.stage || '';
    const advRow = form.querySelector<HTMLElement>('[data-mtrc-advance]');
    if (advRow) advRow.hidden = !!stage && !['new', 'contacted', 'qualified'].includes(stage);
    // the home follows the picked lead only when the form wasn't opened ON a home (a tour belongs to the home it's at)
    if (o.listing_id) return;
    const l = !isNew && sel?.value ? leadOf(ctx, sel.value) : null;
    const home = form.elements.namedItem('listing_id') as HTMLSelectElement | null;
    if (home && l && l.property_interest && [...home.options].some(op => op.value === l.property_interest)) home.value = l.property_interest;
  };
  sel?.addEventListener('change', sync);
  sync();
  if (!loaded) void ctx.ensureLayer('mtr').then(() => {
    if (!form.isConnected) return;
    loaded = true;
    if (sel) { sel.innerHTML = leadOptions(''); sync(); }
  });
  void loadPeople().then(() => { const dl = form.querySelector('#mtrc-people'); if (dl) dl.innerHTML = people(); });
}

export function openNoteForm(ctx: CalendarCtx, l: MtrLead): void {
  sheetForm(ctx, `Note on ${l.name}`, `<label class="mtrc-f">Note<textarea name="note" rows="5" maxlength="4000" data-autofocus placeholder="What happened? (shows on the Pipeline card)"></textarea></label>`, 'Add note', async (f, setErr) => {
    const note = val(f, 'note');
    if (!note) { setErr('Write the note first.'); return false; }
    const r = await api('POST', `/api/mtr/leads/${encodeURIComponent(l.id)}`, { note });
    if (!r.ok) { setErr(errText(r, 'Saving the note')); return false; }
    ctx.toast('Note added');
    await reloadMtr(ctx);
    return true;
  });
}
/** Lost / Ended: an optional reason rides in the SAME write as the move. */
export function openReasonForm(ctx: CalendarCtx, l: MtrLead, to: string): void {
  let cur = l;
  sheetForm(ctx, `${stageLabel(to)} — ${l.name}`, `<label class="mtrc-f">Reason (optional)<textarea name="note" rows="4" maxlength="4000" data-autofocus placeholder="${to === 'lost' ? 'Why did it fall through?' : 'How did the tenancy end?'}"></textarea></label>`, `Move to ${stageLabel(to)}`, async (f, setErr) => {
    const errBox = document.createElement('div');
    const res = await moveLeadStatus(ctx, cur, to, val(f, 'note') || null, errBox, true);
    if (res === 'ok') return true;
    if (res === 'stale') {
      // R115 review (R114 'reason survives 409'): keep the sheet + the typed reason; the next press uses the fresh stage
      const fresh = st.leadCache.get(cur.id);
      if (fresh) cur = fresh;
      setErr(`Someone else moved ${cur.name} to ${stageLabel(cur.stage)} meanwhile — your reason is still here. Press “Move to ${stageLabel(to)}” again to confirm.`);
      return false;
    }
    setErr(errBox.textContent || 'The move didn’t save — try again.');
    return false;
  });
}
export function openRescheduleForm(ctx: CalendarCtx, leadId: string, t: MtrLeadTour): void {
  sheetForm(ctx, 'Reschedule tour', `<label class="mtrc-f">New day and time<input type="datetime-local" name="at" value="${ctx.esc(t.at)}" step="900" data-autofocus></label>`, 'Save', async (f, setErr) => {
    const at = val(f, 'at');
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(at)) { setErr('Pick the new day and time.'); return false; }
    const r = await api('PUT', `/api/mtr/leads/${encodeURIComponent(leadId)}/tours/${encodeURIComponent(t.id)}`, { at });
    if (!r.ok) { setErr(errText(r, 'Rescheduling')); return false; }
    const v = safeLead(r.j && r.j.lead); if (v) st.leadCache.set(v.id, v);
    ctx.toast(`Tour moved to ${ctx.fmt.wmd(at.slice(0, 10))} ${timeLabel(at)}${tourWarn(r)}`);
    await reloadMtr(ctx);
    return true;
  });
}
